/**
 * THE CATCH INSTRUMENT: what the bottom-centre plate says, and the one look that feeds it.
 *
 * -- ONE DETECTOR -----------------------------------------------------------------------------
 *
 * `createCatchCeremony` is the host's single look at the save for the ceremony. It keeps the
 * previous look and hands both to Task 3's `detectCatch`, which is the ONLY judge of whether a
 * fish came aboard; nothing here counts catches for itself. What this file adds is the FACTS
 * the plate prints - weight, the record it beat, XP and coins, level, mastery, the goal either
 * side - read off the same two looks, and the milestone label the event carries when the catch
 * finished a goal, raised the level or reached a mastery rung. That label is what makes Task 3's
 * model reveal an ordinary repeat in full when the player has collapsed ordinary repeats.
 *
 * -- REEL IS PRESENTATION -----------------------------------------------------------------------
 *
 * `reel` records the instant Reel was pressed and nothing else: the model is always
 * `catchModelAt`, Task 3's `catchPresentationAt` with `reelPresentation` applied from that
 * instant. No save, no RNG, no reward and no goal can be reached from this file, and the
 * import fence in catch-instrument.test.ts holds it there.
 *
 * -- ONE HEADING, ONE DETAIL BLOCK --------------------------------------------------------------
 *
 * `catchInstrumentAt` is the plate's pure view: a heading, at most one stamp, two to four short
 * mono lines (the figures; what the catch means; a rung; a level) and one next-action line.
 * Ordinary repeats collapse to a compact strip (the fish, its weight, the two figures); a new
 * species, a beaten record and any milestone get the full plate. The prompt during the pull is
 * the same plate in its first state.
 */
import type { PlayerProfile, PlayerSettings } from '../profile/schema';
import { levelForXp, MASTERY_FULL, MASTERY_TICKS_PER_HOUR, SPECIES, speciesById, VARIANTS, variantById, type SimState } from '../sim/index';
import type { VoyageView } from '../voyage/index';
import { weightWords } from '../hud/book';
import { catchModelAt, catchPhaseStartMs, detectCatch, revealsEvent, type CatchEphemeral, type CatchEvent, type CatchPresentation } from './catch-presentation';
import { currentGoal, type GoalView } from './goals';

export interface CatchFacts {
  readonly speciesId: string;
  readonly name: string;
  /** The form that came aboard ("Fine", "Prize", "Elder"), or null for the plain fish. */
  readonly form: string | null;
  readonly grams: number;
  /** The species' best before this catch; 0 for a first. */
  readonly recordBeforeG: number;
  readonly xpDelta: number;
  readonly coinsDelta: number;
  readonly levelAfter: number;
  readonly levelUp: boolean;
  /** Mastery points in the species after the catch. */
  readonly masteryPoints: number;
  /** The rung this catch crossed, by form name, or null. */
  readonly masteryStep: string | null;
  /** Distinct species landed so far, after this one. */
  readonly speciesFound: number;
  readonly goalBefore: GoalView;
  readonly goalAfter: GoalView;
}

/** The host's ephemeral ceremony state: Task 3's two fields plus the facts the plate prints. */
export interface CatchInstrumentState extends CatchEphemeral { readonly facts: CatchFacts | null; }

export type CatchInstrumentMode = 'hidden' | 'prompt' | 'compact' | 'full';
export interface CatchInstrumentView {
  readonly mode: CatchInstrumentMode;
  readonly speciesId: string | null;
  readonly heading: string;
  readonly stamp: { readonly word: string; readonly tone: 'ink' | 'record' } | null;
  readonly detail: readonly string[];
  readonly next: string;
}

const HIDDEN: CatchInstrumentView = { mode: 'hidden', speciesId: null, heading: '', stamp: null, detail: [], next: '' };

export function goalCompleted(f: CatchFacts): boolean {
  return f.goalBefore.id !== f.goalAfter.id || (f.goalAfter.complete && !f.goalBefore.complete);
}

/** The milestone this catch carries, in a fixed order: the goal, then the level, then the rung. */
export function milestoneOf(f: CatchFacts): string | null {
  if (goalCompleted(f)) return 'Goal done';
  if (f.levelUp) return `Level ${f.levelAfter}`;
  if (f.masteryStep !== null) return `${f.masteryStep} form`;
  return null;
}

/** Whether Reel is answered now: inside the pull or the reel window, not under Relaxed Fishing, and only toward a reveal. */
export function reelable(model: CatchPresentation, settings: PlayerSettings): boolean {
  if (model.event === null || settings.relaxedFishing) return false;
  if (model.phase !== 'pull' && model.phase !== 'reel-window') return false;
  return revealsEvent(model.event, settings);
}

/** The bare Space key, with the ledger shut. Never R (the ledger's Return-to-course key), never Enter on the page. */
export function reelKey(key: string, ledgerOpen: boolean): boolean {
  return key === ' ' && !ledgerOpen;
}

/**
 * Milliseconds into the model's phase: from Task 3's table, or from the Reel press for a reeled
 * reveal. Infinity when there is no catch, so a resting line reads as long settled.
 */
export function catchPhaseMs(model: CatchPresentation, state: CatchInstrumentState, nowMs: number, settings: PlayerSettings): number {
  if (model.event === null) return Number.POSITIVE_INFINITY;
  const elapsed = Math.max(0, nowMs - model.event.atMs);
  const tableStart = catchPhaseStartMs(model.event, model.phase, settings);
  const start = model.phase === 'reveal' && state.reeledAtMs !== null ? Math.min(tableStart, Math.max(0, state.reeledAtMs - model.event.atMs)) : tableStart;
  return Math.max(0, elapsed - start);
}

function masteryWords(points: number): string {
  const ticks = Math.max(0, Math.round(points));
  if (ticks < 60) return 'under a minute';
  const hours = Math.floor(ticks / MASTERY_TICKS_PER_HOUR);
  const minutes = Math.floor((ticks % MASTERY_TICKS_PER_HOUR) / 60);
  if (hours === 0) return `${minutes} min`;
  return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
}
function figures(f: CatchFacts): string { return `+${f.xpDelta} XP, +${f.coinsDelta} coins.`; }
function nextSpeciesLine(level: number): string {
  const next = SPECIES.find((s) => s.minLevel > level);
  return next === undefined ? `Level ${level}.` : `Level ${level}. ${next.name} at level ${next.minLevel}.`;
}
function goalLine(f: CatchFacts): string {
  if (goalCompleted(f)) return f.goalBefore.id === f.goalAfter.id ? `Goal done: ${f.goalAfter.label}.` : `Goal done: ${f.goalBefore.label}. Next: ${f.goalAfter.label}.`;
  const g = f.goalAfter;
  return `${Math.round(g.progress)} of ${g.target} ${g.units} toward ${g.label}.`;
}
function fishName(f: CatchFacts): string { return f.form === null ? f.name : `${f.form} ${f.name}`; }

export function catchInstrumentAt(model: CatchPresentation, facts: CatchFacts | null, settings: PlayerSettings): CatchInstrumentView {
  if (model.event === null || facts === null) return HIDDEN;
  const event = model.event;
  if (model.phase === 'pull' || model.phase === 'reel-window') {
    if (!reelable(model, settings)) return HIDDEN;
    return { mode: 'prompt', speciesId: event.speciesId, heading: 'The line pulls.', stamp: null, detail: ['Same catch, a little more ceremony.'], next: '' };
  }
  if (model.phase !== 'reveal') return HIDDEN;
  if (model.compact) {
    return { mode: 'compact', speciesId: event.speciesId, heading: `${fishName(facts)}, ${weightWords(facts.grams)}`, stamp: null, detail: [figures(facts)], next: '' };
  }
  const record = event.kind === 'record';
  const fresh = event.kind === 'new' || event.kind === 'new-record';
  const milestone = event.milestone ?? milestoneOf(facts);
  const stamp = record ? { word: 'Record', tone: 'record' as const } : fresh ? { word: 'New species', tone: 'ink' as const } : milestone !== null ? { word: milestone, tone: 'ink' as const } : null;
  // The figures; what the catch means (a beaten record, a new species, a rung, or where the
  // mastery stands); a rung when the record or the first fish took its line; a level.
  const step = facts.masteryStep === null ? null : `${facts.masteryStep} ${facts.name} can come up from now on.`;
  const detail: string[] = [`${weightWords(facts.grams)}. ${figures(facts)}`];
  if (record) detail.push(`Heaviest yet; was ${weightWords(facts.recordBeforeG)}.`);
  else if (fresh) detail.push(`Species ${facts.speciesFound} of ${SPECIES.length} found.`);
  else detail.push(step ?? `Mastery ${masteryWords(facts.masteryPoints)} of ${masteryWords(MASTERY_FULL)}.`);
  if ((record || fresh) && step !== null) detail.push(step);
  if (facts.levelUp) detail.push(nextSpeciesLine(facts.levelAfter));
  return { mode: 'full', speciesId: event.speciesId, heading: fishName(facts), stamp, detail, next: goalLine(facts) };
}

// ---- the ceremony: one look at the save, judged by detectCatch ------------------------------

export interface CeremonyLook {
  readonly state: SimState;
  readonly profile: PlayerProfile;
  readonly view: VoyageView;
  readonly nowMs: number;
}
export interface CatchCeremony {
  /** Judge this look against the last. Returns the event that began, or null. The first look baselines. */
  look(l: CeremonyLook): CatchEvent | null;
  /** Reel, at this instant. True when it advanced the presentation. Reaches nothing else. */
  reel(nowMs: number, settings: PlayerSettings): boolean;
  ephemeral(): CatchInstrumentState;
  /** Drop the baseline: a new, imported or restored run makes no ceremony out of its counts. */
  forget(): void;
}

interface Look { readonly state: SimState; readonly goal: GoalView; }

function formOf(before: SimState, after: SimState, speciesId: string): string | null {
  for (const [key, n] of Object.entries(after.variants)) {
    if (n - (before.variants[key] ?? 0) <= 0) continue;
    const cut = key.indexOf(':');
    if (cut < 0 || key.slice(0, cut) !== speciesId) continue;
    return variantById(key.slice(cut + 1))?.name ?? null;
  }
  return null;
}

/** The facts the plate prints, read off the looks either side of one catch. */
export function catchFactsBetween(before: Look, after: Look, event: CatchEvent): CatchFacts {
  const id = event.speciesId;
  const masteryBefore = before.state.mastery[id] ?? 0;
  const masteryAfter = after.state.mastery[id] ?? 0;
  const rung = [...VARIANTS].sort((a, b) => a.masteryAt - b.masteryAt).find((v) => masteryBefore < v.masteryAt && masteryAfter >= v.masteryAt);
  const levelBefore = levelForXp(before.state.xp);
  const levelAfter = levelForXp(after.state.xp);
  return {
    speciesId: id,
    name: speciesById(id)?.name ?? id,
    form: formOf(before.state, after.state, id),
    grams: Math.max(0, after.state.weightG - before.state.weightG),
    recordBeforeG: before.state.records[id] ?? 0,
    xpDelta: Math.max(0, after.state.xp - before.state.xp),
    coinsDelta: Math.max(0, after.state.coins - before.state.coins),
    levelAfter,
    levelUp: levelAfter > levelBefore,
    masteryPoints: masteryAfter,
    masteryStep: rung?.name ?? null,
    speciesFound: Object.values(after.state.catches).filter((n) => n > 0).length,
    goalBefore: before.goal,
    goalAfter: after.goal,
  };
}

export function createCatchCeremony(): CatchCeremony {
  let last: Look | null = null;
  let current: CatchInstrumentState = { catch: null, reeledAtMs: null, facts: null };
  return {
    look(l: CeremonyLook): CatchEvent | null {
      const next: Look = { state: l.state, goal: currentGoal(l.profile, l.state, l.view) };
      const previous = last;
      last = next;
      if (previous === null) return null;
      const event = detectCatch({ origin: 'live', catches: previous.state.catches, records: previous.state.records }, { origin: 'live', catches: next.state.catches, records: next.state.records }, l.nowMs);
      if (event === null) return null;
      const facts = catchFactsBetween(previous, next, event);
      const milestone = milestoneOf(facts);
      current = { catch: milestone === null ? event : { ...event, milestone }, reeledAtMs: null, facts };
      return event;
    },
    reel(nowMs: number, settings: PlayerSettings): boolean {
      if (current.reeledAtMs !== null || !reelable(catchModelAt(nowMs, current, settings), settings)) return false;
      current = { ...current, reeledAtMs: nowMs };
      return true;
    },
    ephemeral: () => current,
    forget(): void {
      last = null;
      current = { catch: null, reeledAtMs: null, facts: null };
    },
  };
}
